import { apiClient } from '@/services/_shared/apiClient';
import ENDPOINTS from '@/utils/endpoints';
import { ENV } from '@/config/env';

// ─── Types ───────────────────────────────────────────────────

export type ChatRoomStatus = 'pending' | 'active' | 'closed';
export type MessageType = 'text' | 'image' | 'system' | 'file';
export type SenderType = 'user' | 'admin' | 'system';

export interface ChatMessage {
  id: string;
  room_id: string;
  sender_id: string | null;
  sender_type: SenderType;
  message: string;
  message_type: MessageType;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  updated_at?: string;
  attachments?: ChatAttachment[];
}

export interface ChatAttachment {
  id: string;
  message_id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
}

export interface ChatRoom {
  id: string;
  type: string;
  app_source: string;
  status: ChatRoomStatus;
  queue_position: number | null;
  has_admin: boolean;
  assigned_at: string | null;
  last_message_at: string | null;
  created_at: string;
  messages?: ChatMessage[];
}

export interface ChatPagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

// ─── Helpers ─────────────────────────────────────────────────

const chatHeaders = () => ({
  'X-App-Source': ENV.APP_SOURCE,
});

// ─── API Methods ─────────────────────────────────────────────

/**
 * GET /active — Check for existing active chat session
 */
export const checkActiveChat = async (): Promise<{
  has_active_chat: boolean;
  room: ChatRoom | null;
}> => {
  const response = await apiClient.get(ENDPOINTS.CHAT_ACTIVE, {
    headers: chatHeaders(),
  });
  return response.data.data;
};

/**
 * POST /start — Start a new chat session
 */
export const startChat = async (): Promise<{
  room: ChatRoom;
  is_new: boolean;
  assigned: boolean;
}> => {
  const response = await apiClient.post(ENDPOINTS.CHAT_START, null, {
    headers: chatHeaders(),
  });
  return response.data.data;
};

/**
 * GET /{roomId}/messages — Fetch paginated messages
 */
export const getMessages = async (
  roomId: string,
  page = 1,
  perPage = 50
): Promise<{
  messages: ChatMessage[];
  pagination: ChatPagination;
}> => {
  const response = await apiClient.get(`${ENDPOINTS.CHAT_BASE}/${roomId}/messages`, {
    params: { page, per_page: perPage },
    headers: chatHeaders(),
  });
  return response.data.data;
};

/**
 * POST /{roomId}/message — Send a text message
 */
export const sendMessage = async (
  roomId: string,
  message: string,
  messageType: MessageType = 'text'
): Promise<ChatMessage> => {
  const response = await apiClient.post(
    `${ENDPOINTS.CHAT_BASE}/${roomId}/message`,
    { message, message_type: messageType },
    { headers: chatHeaders() }
  );
  return response.data.data.message;
};

/**
 * POST /{roomId}/typing — Send typing indicator
 */
export const sendTypingIndicator = async (roomId: string, isTyping: boolean): Promise<void> => {
  await apiClient.post(
    `${ENDPOINTS.CHAT_BASE}/${roomId}/typing`,
    { is_typing: isTyping },
    { headers: chatHeaders() }
  );
};

/**
 * POST /{roomId}/upload-image — Upload image attachment
 */
export const uploadImage = async (roomId: string, image: File): Promise<ChatMessage> => {
  const formData = new FormData();
  formData.append('image', image);

  const response = await apiClient.post(`${ENDPOINTS.CHAT_BASE}/${roomId}/upload-image`, formData, {
    headers: {
      ...chatHeaders(),
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data.data.message;
};

/**
 * Build the URL for fetching a chat attachment (auth-required)
 */
export const getAttachmentUrl = (attachmentId: string): string => {
  return `${ENDPOINTS.CHAT_ATTACHMENT}/${attachmentId}`;
};

/**
 * POST /{roomId}/close — Close the chat session
 */
export const closeChat = async (roomId: string, reason?: string): Promise<void> => {
  await apiClient.post(`${ENDPOINTS.CHAT_BASE}/${roomId}/close`, reason ? { reason } : {}, {
    headers: chatHeaders(),
  });
};
